/**
 * To import directive (script setup syntax):
 * import {vTooltip} from "@/common/directives/tip-directive";
 *
 * To use directive:
 *   v-tooltip="tooltipText"
 *
 * To use directive with modifiers:
 *   v-tooltip.n="tooltipText"
 *
 * To use directive with optional delays:
 *   v-tooltip="{content: 'tooltipText', delay: {show: 500, hide: 500}}"
 *
 * To use directive with condition:
 *   v-tooltip="{content: 'tooltipText', condition: (el, binding) => someCondition(el)}"
 *
 * To use directive with detectContentOverflow (this will add a `data-has-overflow="<boolean>"`
 * attribute to the content element based on if overflow is detected):
 *   v-tooltip="{content: 'tooltipText', detectContentOverflow: true}"
 *
 * Modifier list:
 *   'n', 's', 'w', 'e', 'nw', 'ne' 'sw', 'se'  will position tooltip on top, bottom, left, right of the element
 *   'touch' will enable tooltip on mobile and tablet devices
 *   'white' will add white background to tooltip
 *   'small' has max-width of 250px (default)
 *   'medium' has max-width of 350px
 *   'large' has max-width of 450px
 *   'nowrap' has no max-width and will not wrap text
 *   'delay.show' will add a delay before showing the tooltip (default 500ms)
 *   'delay.hide' will add a delay before hiding the tooltip (default 500ms)
 *   'hidePoint' will hide the tooltip pointer
 *   'allowUnsafeHtml' renders HTML **exactly** as provided, with **no sanitization**. **It must never be used with user-supplied content.**
 */
import { sanitizeHtml } from "@/common/helpers/sanitize";
// Check if the value is a TooltipObject
function isTooltipObject(value) {
    return typeof value === "object" && value !== null && "content" in value;
}
// Helper to clear show timeout
const clearShowTimeout = (el) => {
    if (el._tooltipShowTimeout) {
        clearTimeout(el._tooltipShowTimeout);
        el._tooltipShowTimeout = null;
    }
};
// Helper to clear hide timeout
const clearHideTimeout = (el) => {
    if (el._tooltipHideTimeout) {
        clearTimeout(el._tooltipHideTimeout);
        el._tooltipHideTimeout = null;
    }
};
// Calculate the optimal position for the tooltip before positioning it
const calculateOptimalPosition = (tooltip, el, originalPosition) => {
    const { left, top, width, height } = el.getBoundingClientRect();
    const windowWidth = document.documentElement.clientWidth;
    const windowHeight = document.documentElement.clientHeight;
    let position = originalPosition;
    // Calculate available space in each direction
    const spaceAbove = top;
    const spaceBelow = windowHeight - (top + height);
    const spaceLeft = left;
    const spaceRight = windowWidth - (left + width);
    // Get tooltip dimensions
    const { width: tooltipWidth, height: tooltipHeight } = tooltip.getBoundingClientRect();
    // Define direction constraints and their opposites
    const directionConstraints = [
        { dir: "n", opposite: "s", available: spaceAbove, required: tooltipHeight },
        { dir: "s", opposite: "n", available: spaceBelow, required: tooltipHeight },
        { dir: "w", opposite: "e", available: spaceLeft, required: tooltipWidth },
        { dir: "e", opposite: "w", available: spaceRight, required: tooltipWidth }
    ];
    // Flip directions that don't have enough space
    directionConstraints.forEach(({ dir, opposite, available, required }) => {
        if (position.includes(dir) && available < required) {
            position = position.replace(dir, opposite);
        }
    });
    return position;
};
// Position the tooltip element relative to the target element
const positionTooltip = (tooltip, el, binding) => {
    tooltip.className = tooltip.className.replace(/\brec-tooltip--[nswe]{1,2}\b/gi, "");
    // Reset tooltip position styles
    tooltip.style.top = "";
    tooltip.style.left = "";
    tooltip.style.right = "";
    const positions = ["s", "w", "e", "nw", "ne", "sw", "se"];
    const modifiers = normalizeModifiers(binding);
    let position = positions.find((pos) => modifiers[pos]) || "n";
    // If the page is RTL, swap the west and east positions
    if (window.Ramius.is_rtl) {
        const rtlMap = {
            w: "e",
            e: "w",
            nw: "ne",
            ne: "nw",
            sw: "se",
            se: "sw"
        };
        if (position in rtlMap) {
            position = rtlMap[position];
        }
    }
    const { left, top, width, height } = el.getBoundingClientRect();
    const windowWidth = document.documentElement.clientWidth;
    if (width <= 40) {
        tooltip.classList.add("rec-tooltip--small-target");
    }
    // Calculate the optimal position before applying it
    const optimalPosition = calculateOptimalPosition(tooltip, el, position);
    // Apply the optimal position
    applyPosition(tooltip, optimalPosition, left, top, width, height, windowWidth);
};
// Apply position to tooltip
const applyPosition = (tooltip, position, left, top, width, height, windowWidth) => {
    // Add scroll offsets to position relative to document instead of viewport
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrollLeft = window.scrollX || document.documentElement.scrollLeft;
    switch (position) {
        case "s":
            tooltip.classList.add("rec-tooltip--s");
            tooltip.style.top = `${top + height + scrollTop}px`;
            tooltip.style.left = `${left + width / 2 + scrollLeft}px`;
            break;
        case "w":
            tooltip.classList.add("rec-tooltip--w");
            tooltip.style.top = `${top + height / 2 + scrollTop}px`;
            tooltip.style.left = `${left + scrollLeft}px`;
            break;
        case "e":
            tooltip.classList.add("rec-tooltip--e");
            tooltip.style.top = `${top + height / 2 + scrollTop}px`;
            tooltip.style.left = `${left + width + scrollLeft}px`;
            break;
        case "nw":
            tooltip.classList.add("rec-tooltip--nw");
            tooltip.style.top = `${top + scrollTop}px`;
            tooltip.style.right = `${windowWidth - left - width + scrollLeft}px`;
            break;
        case "ne":
            tooltip.classList.add("rec-tooltip--ne");
            tooltip.style.top = `${top + scrollTop}px`;
            tooltip.style.left = `${left + scrollLeft}px`;
            break;
        case "sw":
            tooltip.classList.add("rec-tooltip--sw");
            tooltip.style.top = `${top + height + scrollTop}px`;
            tooltip.style.right = `${windowWidth - left - width + scrollLeft}px`;
            break;
        case "se":
            tooltip.classList.add("rec-tooltip--se");
            tooltip.style.top = `${top + height + scrollTop}px`;
            tooltip.style.left = `${left + scrollLeft}px`;
            break;
        default:
            tooltip.classList.add("rec-tooltip--n");
            tooltip.style.top = `${top + scrollTop}px`;
            tooltip.style.left = `${left + width / 2 + scrollLeft}px`;
            break;
    }
};
const normalizeModifiers = (binding) => {
    const normalizedModifiers = { ...binding.modifiers };
    if (isTooltipObject(binding.value)) {
        (binding.value.modifiers || []).forEach((m) => (normalizedModifiers[m] = true));
    }
    return normalizedModifiers;
};
// Create the tooltip element
const createTooltip = (el, binding) => {
    const modifiers = normalizeModifiers(binding);
    if (!modifiers.touch && (el.tooltip || window.Ramius.is_mobile || window.Ramius.is_tablet))
        return;
    let tooltipContent = "";
    if (isTooltipObject(binding.value)) {
        tooltipContent = binding.value.content || "";
    }
    else if (typeof binding.value === "string") {
        tooltipContent = binding.value;
    }
    const tooltip = document.createElement("div");
    tooltip.setAttribute("role", "tooltip");
    const id = (isTooltipObject(binding.value) && binding.value?.id) || "rec-tooltip-container";
    tooltip.setAttribute("id", id);
    tooltip.className = "rec-tooltip";
    tooltip.innerHTML = modifiers.allowUnsafeHtml ? tooltipContent : sanitizeHtml(tooltipContent, "tooltip");
    // Add modifiers to tooltip
    if (modifiers.white)
        tooltip.classList.add("rec-tooltip--white");
    if (modifiers.xsmall)
        tooltip.classList.add("rec-tooltip--xsmall");
    if (modifiers.small)
        tooltip.classList.add("rec-tooltip--small");
    if (modifiers.medium)
        tooltip.classList.add("rec-tooltip--medium");
    if (modifiers.large)
        tooltip.classList.add("rec-tooltip--large");
    if (modifiers.nowrap)
        tooltip.classList.add("rec-tooltip--nowrap");
    if (modifiers.hidePoint)
        tooltip.classList.add("rec-tooltip--hide-point");
    // Save the tooltip element as a property of the target element
    el.tooltip = tooltip;
    return tooltip;
};
// Set up event listeners to handle and show/hide
const attachTooltip = (tooltip, el, binding) => {
    // Calculate delays once at setup time
    let showDelay;
    let hideDelay = 100; // Default hide delay
    if (isTooltipObject(binding.value) && binding.value.delay) {
        showDelay = binding.value.delay.show;
        hideDelay = binding.value.delay.hide ?? 100;
    }
    // If delay modifier is present, use default show delay
    if (binding.modifiers.delay) {
        showDelay = showDelay ?? 500;
        hideDelay = hideDelay ?? 500;
    }
    const show = () => {
        showTooltip(tooltip, el, binding, showDelay);
    };
    const hide = () => {
        hideTooltip(tooltip, el, hideDelay);
    };
    // Check if focus is on trigger or tooltip, hide if not
    const handleFocusChange = () => {
        const activeElement = document.activeElement;
        const isOnTrigger = el.contains(activeElement);
        const isOnTooltip = tooltip.contains(activeElement);
        // If tooltip is visible and focus is elsewhere, hide it
        if (tooltip.style.opacity === "1" && !isOnTrigger && !isOnTooltip) {
            hide();
        }
    };
    const events = [
        { type: "mouseenter", handler: show },
        { type: "focus", handler: show },
        { type: "mouseleave", handler: hide },
        { type: "blur", handler: hide }
    ];
    events.forEach((event) => {
        const { type, handler } = event;
        el.removeEventListener(type, handler);
        el.addEventListener(type, handler);
    });
    // Add mouseenter and mouseleave to tooltip itself to keep it visible when hovering
    tooltip.addEventListener("mouseenter", () => {
        clearHideTimeout(el);
    });
    tooltip.addEventListener("mouseleave", hide);
    // Hide tooltip when focus moves elsewhere
    el._tooltipFocusHandler = handleFocusChange;
    document.addEventListener("focusin", el._tooltipFocusHandler);
    tooltip.addEventListener("transitionend", () => {
        if (tooltip.style.opacity === "0") {
            removeTooltip(tooltip);
        }
    });
    // Add scroll event listener to hide tooltip on scroll
    const scrollHandler = () => {
        // Clear any pending timeouts
        clearShowTimeout(el);
        clearHideTimeout(el);
        // Hide immediately without delay
        tooltip.style.opacity = "0";
        tooltip.style.scale = "0.95";
    };
    el._tooltipScrollHandler = scrollHandler;
    window.addEventListener("scroll", scrollHandler, true);
};
// Display tooltip in DOM with proper positioning and visibility
const displayTooltip = (tooltip, el, binding) => {
    const id = (isTooltipObject(binding.value) && binding.value?.id) || "rec-tooltip-container";
    el.setAttribute("aria-describedby", id);
    document.body.appendChild(tooltip);
    positionTooltip(tooltip, el, binding);
    tooltip.style.opacity = "1";
    tooltip.style.scale = "1";
    if (isTooltipObject(binding.value) && binding.value.detectContentOverflow) {
        setupTooltipOverflowDetection(tooltip);
    }
};
// Show tooltip
const showTooltip = (tooltip, el, binding, showDelay) => {
    // Clear any pending timeout
    clearShowTimeout(el);
    clearHideTimeout(el);
    if (isTooltipObject(binding.value) && binding.value.condition && !binding.value.condition(el, binding)) {
        return;
    }
    if (tooltip.innerHTML.length > 0) {
        if (showDelay !== undefined) {
            // timeout to show tooltip
            el._tooltipShowTimeout = setTimeout(() => {
                displayTooltip(tooltip, el, binding);
                el._tooltipShowTimeout = null;
            }, showDelay);
        }
        else {
            displayTooltip(tooltip, el, binding);
        }
    }
};
// Hide tooltip
const hideTooltip = (tooltip, el, hideDelay) => {
    // Clear any pending timeout
    clearShowTimeout(el);
    clearHideTimeout(el);
    el.removeAttribute("aria-describedby");
    el._tooltipHideTimeout = setTimeout(() => {
        tooltip.style.opacity = "0";
        tooltip.style.scale = "0.95";
        el._tooltipHideTimeout = null;
    }, hideDelay);
};
/**
 * Set up overflow detection for tooltip content.
 * Checks if content overflows and sets data attribute accordingly.
 * Looks for elements with class pattern `*__content`
 */
const setupTooltipOverflowDetection = (tooltip) => {
    const contentEl = tooltip.querySelector(`.${tooltip.id}`);
    if (contentEl) {
        // Subtract 1 pixel to account for potential rounding issues
        const hasOverflow = contentEl.scrollHeight - contentEl.clientHeight > 1;
        contentEl.setAttribute("data-has-overflow", String(hasOverflow));
    }
};
// Remove tooltip
const removeTooltip = (tooltip) => {
    tooltip.remove();
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let onTransitionEnd;
const vTooltip = {
    mounted(el, binding) {
        const tooltip = createTooltip(el, binding);
        if (tooltip) {
            attachTooltip(tooltip, el, binding);
        }
        onTransitionEnd = () => {
            if (!el.tooltip)
                return;
            positionTooltip(el.tooltip, el, binding);
        };
        el.ontransitionend = onTransitionEnd;
    },
    updated(el, binding) {
        let tooltipContent = "";
        if (isTooltipObject(binding.value)) {
            tooltipContent = binding.value.content || "";
        }
        else if (typeof binding.value === "string") {
            tooltipContent = binding.value;
        }
        if (el.tooltip) {
            // If content is empty, hide the tooltip and clear its content
            if (!tooltipContent) {
                clearShowTimeout(el);
                clearHideTimeout(el);
                el.removeAttribute("aria-describedby");
                el.tooltip.innerHTML = "";
                el.tooltip.style.opacity = "0";
                el.tooltip.style.scale = "0.95";
                // Remove from DOM if currently attached
                if (document.body.contains(el.tooltip)) {
                    removeTooltip(el.tooltip);
                }
                return;
            }
            const modifiers = normalizeModifiers(binding);
            el.tooltip.innerHTML = modifiers.allowUnsafeHtml ? tooltipContent : sanitizeHtml(tooltipContent, "tooltip");
            // If tooltip was removed from DOM but element is still hovered, re-add it
            if (!document.body.contains(el.tooltip) && el.matches(":hover")) {
                displayTooltip(el.tooltip, el, binding);
            }
            else {
                positionTooltip(el.tooltip, el, binding);
                // Re-run overflow detection if tooltip is visible and detection is enabled
                if (document.body.contains(el.tooltip) &&
                    el.tooltip.style.opacity === "1" &&
                    isTooltipObject(binding.value) &&
                    binding.value.detectContentOverflow) {
                    setupTooltipOverflowDetection(el.tooltip);
                }
            }
        }
    },
    unmounted(el) {
        el.removeEventListener("transitionend", onTransitionEnd, true);
        // Remove focus event listener
        if (el._tooltipFocusHandler) {
            document.removeEventListener("focusin", el._tooltipFocusHandler);
            el._tooltipFocusHandler = undefined;
        }
        // Remove scroll event listener
        if (el._tooltipScrollHandler) {
            window.removeEventListener("scroll", el._tooltipScrollHandler, true);
            el._tooltipScrollHandler = undefined;
        }
        // Clear any pending timeouts
        clearShowTimeout(el);
        clearHideTimeout(el);
        if (el.tooltip) {
            removeTooltip(el.tooltip);
        }
    }
};
export { vTooltip };
